import React from 'react';
import { Trophy, Target, TrendingUp, Play } from 'lucide-react';

const NewSkillBrick = ({ newSkill, handleFocus }) => {
  // Early return if newSkill is not provided
  if (!newSkill) {
    return null;
  }

  const getProgressConfig = () => {
    const totalProgress = Object.values(newSkill || {}).reduce((sum, value) => {
      return sum + (typeof value === 'number' ? value : 0);
    }, 0) - (newSkill.skill_id || 0); // Exclude skill_id from total

    if (totalProgress === 0) {
      return {
        bg: 'bg-gradient-to-r from-red-500 to-pink-500',
        hover: 'hover:from-red-600 hover:to-pink-600',
        label: 'Starting',
        emoji: '🆕',
        ring: 'focus:ring-red-500',
        glow: 'hover:shadow-red-500/25'
      };
    } else if (totalProgress < 10) {
      return {
        bg: 'bg-gradient-to-r from-yellow-500 to-orange-500',
        hover: 'hover:from-yellow-600 hover:to-orange-600',
        label: 'Learning',
        emoji: '🎯',
        ring: 'focus:ring-yellow-500',
        glow: 'hover:shadow-yellow-500/25'
      };
    } else if (totalProgress < 25) {
      return {
        bg: 'bg-gradient-to-r from-blue-500 to-purple-500',
        hover: 'hover:from-blue-600 hover:to-purple-600',
        label: 'Developing',
        emoji: '📈',
        ring: 'focus:ring-blue-500',
        glow: 'hover:shadow-blue-500/25'
      };
    } else {
      return {
        bg: 'bg-gradient-to-r from-green-500 to-emerald-500',
        hover: 'hover:from-green-600 hover:to-emerald-600',
        label: 'Advanced',
        emoji: '🏆',
        ring: 'focus:ring-green-500',
        glow: 'hover:shadow-green-500/25'
      };
    }
  };

  const config = getProgressConfig();

  // Get the key skill metrics for display
  const getKeyMetrics = () => {
    const metrics = [];
    
    if ((newSkill.competed || 0) > 0) {
      metrics.push({ icon: Trophy, value: newSkill.competed, label: 'Competed' });
    }
    if ((newSkill.in_routine || 0) > 0) {
      metrics.push({ icon: Target, value: newSkill.in_routine, label: 'In Routine' });
    }
    if ((newSkill.to_bed || 0) > 0) {
      metrics.push({ icon: TrendingUp, value: newSkill.to_bed, label: 'To Bed' });
    }
    if ((newSkill.in_rig || 0) > 0 && metrics.length === 0) {
      metrics.push({ icon: Play, value: newSkill.in_rig, label: 'In Rig' });
    }
    
    return metrics.slice(0, 3); // Show max 3 metrics
  };

  const keyMetrics = getKeyMetrics();

  // Calculate completion percentage for progress stages
  const getCompletionPercentage = () => {
    const stages = ['in_rig', 'to_pit', 'mat_with_catch', 'to_mat', 'to_bed', 'in_combination', 'in_routine', 'competed'];
    const completedStages = stages.filter(stage => (newSkill[stage] || 0) > 0).length;
    return (completedStages / stages.length) * 100;
  };

  const completionPercentage = getCompletionPercentage();

  return (
    <div
      className={`
        ${config.bg} ${config.hover} ${config.ring} ${config.glow}
        border-2 border-white/20 rounded-2xl p-5 my-3 
        cursor-pointer transition-all duration-300 ease-out
        transform hover:scale-105 hover:shadow-xl
        focus:outline-none focus:ring-2 focus:ring-offset-2
        active:scale-95 select-none backdrop-blur-sm
        shadow-lg border-b-4 border-b-black/20
      `}
      onClick={() => handleFocus(newSkill)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleFocus(newSkill);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`${newSkill.name || 'Unknown Skill'} skill - ${config.label} level`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{config.emoji}</span>
          <h2 className="text-lg font-bold text-white drop-shadow-lg">
            {newSkill.name || 'Unknown Skill'}
          </h2>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-base font-semibold text-white/90 bg-black/20 px-2 py-1 rounded-full">
            {config.label}
          </span>
        </div>
      </div>

      {/* Key Metrics */}
      {keyMetrics.length > 0 && (
        <div className="flex gap-3 mb-4">
          {keyMetrics.map((metric, index) => (
            <div key={index} className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
              <metric.icon className="h-3 w-3 text-white" />
              <span className="text-white text-base font-medium">{metric.value}</span>
              <span className="text-white/80 text-base">{metric.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-base text-white/90">
          <span>Learning Progress:</span>
          <span>{Math.round(completionPercentage)}%</span>
        </div>
        <div className="w-full bg-white/20 rounded-full h-2">
          <div
            className="bg-white/60 h-2 rounded-full transition-all duration-300 shadow-sm"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>

      {/* Stage Indicators */}
      <div className="flex mt-3 space-x-1">
        {['in_rig', 'to_pit', 'mat_with_catch', 'to_mat', 'to_bed', 'in_combination', 'in_routine', 'competed'].map((stage, index) => (
          <div
            key={stage}
            className={`h-1.5 w-full rounded-full transition-all duration-300 ${
              (newSkill[stage] || 0) > 0 
                ? 'bg-white/60 shadow-sm' 
                : 'bg-white/20'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default NewSkillBrick;